import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography
} from "@material-ui/core";
import LinkIcon from "@material-ui/icons/Link";
import SearchIcon from "@material-ui/icons/Search";
import { toast } from "react-toastify";

import toastError from "../../errors/toastError";
import api from "../../services/api";

const LinkExistingOsModal = ({ open, onClose, ticket }) => {
  const [search, setSearch] = useState("");
  const [orders, setOrders] = useState([]);
  const [existingLinks, setExistingLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linkingId, setLinkingId] = useState(null);

  useEffect(() => {
    if (!open || !ticket?.id) return undefined;

    let active = true;
    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const trimmedSearch = search.trim();
        const params = {
          per_page: 20,
          ...(trimmedSearch
            ? /^\d+$/.test(trimmedSearch)
              ? { id: trimmedSearch }
              : { q: trimmedSearch }
            : { abertas: true })
        };
        const [ordersResponse, linksResponse] = await Promise.all([
          api.get("/os/ordens-servico", { params }),
          api.get(`/tickets/${ticket.id}/os`)
        ]);

        if (!active) return;
        setOrders(ordersResponse.data?.data || []);
        setExistingLinks(linksResponse.data?.data || []);
      } catch (err) {
        if (active) toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    }, search.trim() ? 350 : 0);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [open, search, ticket]);

  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const linkedIds = useMemo(
    () => new Set(existingLinks.map(link => String(link.osId))),
    [existingLinks]
  );

  const linkOrder = async order => {
    setLinkingId(order.id);
    try {
      await api.post(`/tickets/${ticket.id}/os-links`, {
        os_id: order.id
      });
      toast.success(`OS #${order.id} vinculada ao atendimento.`);
      setExistingLinks(current => [
        ...current,
        { id: `new-${order.id}`, osId: order.id, ordem: order }
      ]);
      window.dispatchEvent(
        new CustomEvent("ticket-os-links-updated", {
          detail: { ticketId: ticket.id }
        })
      );
    } catch (err) {
      toastError(err);
    } finally {
      setLinkingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Vincular atendimento a uma OS existente</DialogTitle>
      <DialogContent dividers>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          size="small"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Número da OS, cliente ou descrição"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: loading ? <CircularProgress size={20} /> : null
          }}
        />

        <List>
          {orders.map(order => {
            const linked = linkedIds.has(String(order.id));
            return (
              <ListItem key={order.id} divider>
                <ListItemText
                  primary={
                    <>
                      <strong>OS #{order.id}</strong>
                      {order.status?.nome && (
                        <Chip
                          label={order.status.nome}
                          size="small"
                          color={order.data_fechada ? "default" : "primary"}
                          style={{ marginLeft: 8 }}
                        />
                      )}
                    </>
                  }
                  secondary={
                    <>
                      {order.cliente?.razao ||
                        order.cliente?.fantasia ||
                        "Cliente não informado"}
                      {" · "}
                      {order.descricao_curta}
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <Button
                    color="primary"
                    variant={linked ? "outlined" : "contained"}
                    startIcon={
                      linkingId === order.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <LinkIcon />
                      )
                    }
                    disabled={linked || Boolean(linkingId)}
                    onClick={() => linkOrder(order)}
                  >
                    {linked ? "Vinculada" : "Vincular"}
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>

        {!loading && !orders.length && (
          <Typography align="center" color="textSecondary">
            Nenhuma OS encontrada.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkExistingOsModal;
